/**
 * Cada compra o venta de dolares, con lo que dejo.
 *
 * QUE PREGUNTA CONTESTA
 * ---------------------
 * "¿Me convino ese cambio?"
 *
 * El panel de arriba dice, por ejemplo, "$ 300.000 de cambiar divisas", sumado.
 * Esta tabla es de donde sale ese numero: operacion por operacion, la cotizacion
 * que se pacto contra la que tenia el sistema ese dia, y la diferencia en pesos
 * entre las dos.
 *
 * Una diferencia negativa NO es un error ni una perdida del negocio: al comprar
 * dolares significa que se pagaron mas caros que el sistema, que a veces es la
 * unica forma de conseguir billetes; al venderlos, que se vendieron mas baratos.
 * Por eso se dice con un badge y no pintando el numero: el numero es del color
 * de su moneda y de ninguno mas. El badge mira que moneda salio: antes decia
 * «MÁS CARO» siempre, y una venta de dolares por debajo del sistema decia lo
 * contrario de lo que paso.
 */
import { ArrowRight } from "lucide-react";

import { Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { formatearFecha } from "@/lib/utils";
import { aNumero, type FilaCambioDivisa } from "@/lib/tipos";

import { textoCotizacion } from "@/components/caja/logica";

const COLUMNAS = 5;

export function CambiosDivisa({ filas }: { filas: ReadonlyArray<FilaCambioDivisa> }): JSX.Element {
  const total = filas.reduce((a, f) => a + aNumero(f.diferencia_ars), 0);

  return (
    <Card padding="none" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Table densidad="compacta" alto="lleno">
        <TableHeader>
          <TableRow>
            <TableHead>Fecha</TableHead>
            <TableHead>De dónde a dónde</TableHead>
            <TableHead className="text-right">Entregó</TableHead>
            <TableHead className="text-right">Recibió</TableHead>
            <TableHead className="text-right">Diferencia</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.length === 0 ? (
            <TableVacia
              colSpan={COLUMNAS}
              mensaje="En este período no se compraron ni vendieron dólares."
            />
          ) : (
            filas.map((f) => <FilaCambio key={String(f.id)} fila={f} />)
          )}
        </TableBody>
        {filas.length > 0 ? (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={COLUMNAS - 1}>
                Lo que dejaron los cambios del período
                <span className="pl-2 font-normal tabular-nums text-muted-foreground">
                  {filas.length} {filas.length === 1 ? "operación" : "operaciones"}
                </span>
              </TableCell>
              <TableCellImporte monto={total} moneda="ARS" />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </Card>
  );
}

function FilaCambio({ fila }: { fila: FilaCambioDivisa }): JSX.Element {
  const diferencia = aNumero(fila.diferencia_ars);
  const pactada = aNumero(fila.cotizacion_operacion);
  const sistema = aNumero(fila.cotizacion_sistema);

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap tabular-nums">{formatearFecha(fila.fecha)}</TableCell>
      <TableCell className="text-sm">
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <span>{fila.origen}</span>
          <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">a</span>
          <span>{fila.destino}</span>
        </span>
        {pactada > 0 ? (
          <span className="block text-xs text-muted-foreground">
            A <span className="tabular-nums">$ {textoCotizacion(pactada)}</span>
            {sistema > 0 ? (
              <>
                , el sistema tenía{" "}
                <span className="tabular-nums">$ {textoCotizacion(sistema)}</span>
              </>
            ) : null}
          </span>
        ) : null}
      </TableCell>
      <TableCellImporte
        monto={aNumero(fila.importe_origen)}
        moneda={fila.moneda_origen}
        tamano="sm"
      />
      <TableCellImporte
        monto={aNumero(fila.importe_destino)}
        moneda={fila.moneda_destino}
        tamano="sm"
      />
      <TableCell className="text-right">
        <span className="inline-flex items-center justify-end gap-2 whitespace-nowrap">
          {diferencia < 0 ? (
            <Badge variant="destructive">
              {fila.moneda_origen === "USD" ? "MÁS BARATO" : "MÁS CARO"}
            </Badge>
          ) : null}
          <Importe monto={diferencia} moneda="ARS" tamano="sm" signoExplicito />
        </span>
      </TableCell>
    </TableRow>
  );
}
